import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const categoryIcons: Record<string, string> = {
  'Shoes': 'ri-footprint-line',
  'Hoodies/Sweaters': 'ri-shirt-line',
  'T-Shirts': 'ri-t-shirt-line',
  'Jackets': 'ri-t-shirt-line',
  'Pants/Shorts': 'ri-checkbox-blank-line',
  'Headwear': 'ri-user-3-line',
  'Sets': 'ri-stack-line',
  'Underwear/Underpants': 'ri-checkbox-blank-line',
  'Jersey': 'ri-shirt-line',
  'Accessories': 'ri-vip-diamond-line',
  'Bags': 'ri-shopping-bag-line',
  'Belts': 'ri-link',
  'Perfumes': 'ri-drop-line',
  'Other Stuff': 'ri-more-line',
};

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-table-line text-base md:text-lg text-background-50"></i>
            </div>
            <span className={`font-heading text-base md:text-lg font-semibold transition-colors ${
              isScrolled ? 'text-foreground-950' : 'text-background-50'
            }`}>
              Superbuy Spreadsheet
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
                }`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 py-2 max-h-96 overflow-y-auto">
                  <div className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                    Shop by Category
                  </div>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 text-foreground-500">
                        <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-sm`}></i>
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/superbuy-spreadsheet-guide"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              Complete Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              Resources
            </a>
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                isScrolled
                  ? 'border-primary-500 text-primary-600 hover:bg-primary-50'
                  : 'border-background-50/60 text-background-50 hover:bg-white/10'
              }`}
            >
              superbuy sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              buy it now
              <i className="ri-arrow-right-line ml-1.5 text-xs"></i>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer relative z-50 ${
              isScrolled || isMobileMenuOpen ? 'text-foreground-950' : 'text-background-50'
            }`}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={`text-xl transition-transform duration-200 ${isMobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Fullscreen Menu */}
        <div
          className={`md:hidden fixed inset-0 top-0 z-40 bg-background-50 transition-all duration-300 ${
            isMobileMenuOpen
              ? 'opacity-100 pointer-events-auto'
              : 'opacity-0 pointer-events-none'
          }`}
        >
          <div className="h-full overflow-y-auto pt-20 pb-8 px-4">
            {/* Primary Actions */}
            <div className="flex flex-col gap-2 mb-8">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full px-5 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold text-center hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                buy it now
                <i className="ri-arrow-right-line ml-2 text-xs"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full px-5 py-3.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold text-center hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                superbuy sheet
              </a>
            </div>

            {/* Categories Section */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className="ri-grid-line text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-950">Shop by Category</span>
                <span className="text-xs text-foreground-400">14 collections</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-3 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer group"
                  >
                    <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-background-50 text-foreground-500 group-hover:text-primary-600 transition-colors">
                      <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-sm`}></i>
                    </div>
                    <span className="text-xs font-medium text-foreground-700 group-hover:text-foreground-950 transition-colors leading-tight">
                      {cat.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-compass-3-line text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-950">Quick Links</span>
              </div>
              <div className="space-y-1">
                <Link
                  to="/superbuy-spreadsheet-guide"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                    <i className="ri-book-open-line text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">Complete Guide 2026</p>
                    <p className="text-xs text-foreground-400">In-depth tutorial & walkthrough</p>
                  </div>
                </Link>
                {articles.slice(0, 4).map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                      <i className="ri-article-line text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{article.category}</p>
                      <p className="text-xs text-foreground-400">{article.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Bottom branding */}
            <div className="pt-4 border-t border-background-200/70 text-center">
              <p className="text-xs text-foreground-400">
                Superbuy Spreadsheet &copy; 2026
              </p>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}